import {
  questionDialogFormInitial,
  choiceTemplateDialogFormInitial,
  hookDialogFormInitial,
  matrixChoiceDialogFormInitial,
} from './initial-state';

const initialState = () => ({
  // TABS
  tabActive: 'setup',

  selectorPageSize: 100,

  // SURVEY STATE
  survey: {},
  surveyLoading: false,
  surveyLoadSuccess: false,
  surveyLoadError: null,
  surveySaving: false,
  surveySaveSuccess: false,
  surveySaveError: null,

  surveyList: [],
  surveyListLoading: false,
  surveyListSuccess: false,
  surveyListError: null,
  surveyListFilter: { title: null, state: 'ALL' },
  surveyListPage: 1,
  surveyListTotalPages: 1,

  // QUESTIONS
  questionList: [],
  questionsLoading: false,
  questionsLoadSuccess: false,
  questionsLoadError: null,
  questionsSaving: false,
  questionsSaveSuccess: false,
  questionsSaveError: null,
  questionsPage: 0,
  questionsTotalPages: 0,

  // CHOICE TEMPLATES
  choiceTemplateList: [],
  choiceTemplatesLoading: false,
  choiceTemplatesLoadSuccess: false,
  choiceTemplatesLoadError: null,
  choiceTemplatesSaving: false,
  choiceTemplatesSaveSuccess: false,
  choiceTemplatesSaveError: null,
  choiceTemplatesPage: 0,
  choiceTemplatesTotalPages: 0,

  choiceList: [],
  choicesLoading: false,
  choicesLoadSuccess: false,
  choicesLoadError: null,
  choicesPage: 0,
  choicesTotalPages: 0,

  // QUESTION CATEGORIES
  questionCategoryList: [],
  questionCategoriesLoading: false,
  questionCategoriesLoadSuccess: false,
  questionCategoriesLoadError: null,

  questionCategoryWithQuestionsList: [],
  questionCategoriesWithQuestionsLoading: false,
  questionCategoriesWithQuestionsLoadSuccess: false,
  questionCategoriesWithQuestionsLoadError: null,

  questionCategoriesCreating: false,
  questionCategoriesCreateSuccess: false,
  questionCategoriesCreateError: null,

  questionCategoryDialogVisible: false,

  // CHOICE CATEGORIES
  choiceCategoryList: [],
  choiceCategoriesLoading: false,
  choiceCategoriesLoadSuccess: false,
  choiceCategoriesLoadError: null,
  choiceCategoriesCreating: false,
  choiceCategoriesCreateSuccess: false,
  choiceCategoriesCreateError: null,

  choiceCategoryDialogVisible: false,

  // HOOKS
  hookList: [],
  hooksLoading: false,
  hooksLoadSuccess: false,
  hooksLoadError: null,
  hooksCreating: false,
  hooksCreateSuccess: false,
  hooksCreateError: null,
  hooksSaving: false,
  hooksSaveSuccess: false,
  hooksSaveError: null,
  hooksPage: 0,
  hooksTotalPages: 0,

  // MATRIX CHOICES (MUSIC QUESTIONS)
  matrixChoiceList: [],
  matrixChoicesLoading: false,
  matrixChoicesLoadSuccess: false,
  matrixChoicesLoadError: null,
  matrixChoicesCreating: false,
  matrixChoicesCreateSuccess: false,
  matrixChoicesCreateError: null,
  matrixChoicesSaving: false,
  matrixChoicesSaveSuccess: false,
  matrixChoicesSaveError: null,
  matrixChoicesPage: 0,
  matrixChoicesTotalPages: 0,

  // CANVAS STATE
  canvasQuestions: [],
  canvasQuestionsLoading: false,
  canvasQuestionsLoadSuccess: false,
  canvasQuestionsLoadError: null,
  canvasQuestionsUpdating: false,
  canvasQuestionsUpdateSuccess: false,
  canvasQuestionsUpdateError: null,

  canvasSelectedQuestionId: null,

  // SELECTORS STATE
  selectorSelectedQuestionCategory: null,
  selectorQuestionSearchText: '',
  selectorSelectedTemplateId: null,
  selectorSelectedTemplateCategory: null,
  selectorTemplateSearchText: '',
  selectorSelectedChoiceCategory: null,
  selectorChoiceSearchText: '',
  selectorHookSearchText: '',
  selectorMatrixChoiceSearchText: '',

  // QUESTION DIALOG STATE
  questionDialogVisible: false,
  questionDialogAddToCanvas: false,
  questionDialogForm: { ...questionDialogFormInitial },

  // QUESTION CONDITIONAL LOGIC DIALOG STATE
  questionConditionalLogicDialogVisible: false,
  questionConditionalLogicDialogQuestion: null,

  // HOOK DIALOG STATE
  hookDialogVisible: false,
  hookDialogForm: { ...hookDialogFormInitial },

  // MATRIX CHOICE SAVE DIALOG STATE
  matrixChoiceDialogVisible: false,
  matrixChoiceDialogForm: { ...matrixChoiceDialogFormInitial },

  // MATRIX CONDITIONAL LOGIC DIALOG STATE
  matrixConditionalLogicDialogVisible: false,
  matrixConditionalLogicDialogMatrixChoice: null,

  // CHOICE TEMPLATE DIALOG STATE
  choiceTemplateDialogVisible: false,
  choiceTemplateDialogIsScale: false,
  choiceTemplateDialogChoices: [],
  choiceTemplateDialogForm: { ...choiceTemplateDialogFormInitial },

  choiceTypes: ['SELECT', 'MULTI_SELECT', 'SCALE'],

  questionTypeList: [
    { title: 'Text input', value: 'TEXT' },
    { title: 'Positive / Negative scale', value: 'SCALE' },
    { title: 'Single selection', value: 'SELECT' },
    { title: 'Multiple selection', value: 'MULTI_SELECT' },
    { title: 'Opt in', value: 'OPT_IN' },
    { title: 'Permission to process', value: 'PERMISSION' },
    { title: 'Age consent', value: 'AGE_CONSENT' },
    { title: 'Radio group', value: 'RADIOGROUP' },
    { title: 'Comment', value: 'COMMENT' },
    { title: 'Sortable / Rank', value: 'SORTABLE' },
    { title: 'Date', value: 'DATE' },
    { title: 'Time', value: 'TIME' },
    { title: 'URL', value: 'URL' },
    { title: 'Password', value: 'PASSWORD' },
    { title: 'Range', value: 'RANGE' },
    { title: 'Telephone', value: 'TEL' },
    { title: 'Email', value: 'EMAIL' },
    { title: 'Media', value: 'MEDIA' },
    { title: 'Media Group', value: 'MEDIA' },
  ],

  questionProfileFieldList: [
    { label: 'Age', value: 'age' },
    { label: 'Age Range', value: 'age_range' },
    { label: 'Country', value: 'country' },
    { label: 'Dwelling Type', value: 'dwelling_type' },
    { label: 'Education', value: 'education' },
    { label: 'Ethnicity', value: 'ethnicity' },
    { label: 'Event', value: 'event' },
    { label: 'Gender', value: 'gender' },
    { label: 'Income', value: 'income' },
    { label: 'Language', value: 'language' },
    { label: 'Location', value: 'location' },
    { label: 'Name', value: 'name' },
    { label: 'Product', value: 'product' },
    { label: 'Residential Status', value: 'residential_status' },
  ],
});

export default initialState;



// WEBPACK FOOTER //
// ./src/store/modules/surveybuilder/state.js